import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { MessageCircleMore } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserFeedback } from "@/hooks/api/useUserFeedback";

export function UserFeedbackButton() {
  const { t } = useTranslation(["common"]);
  const { submitUserFeedback, isSubmitting } = useUserFeedback();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [description, setDescription] = useState("");
  const MAX_CHARS = 500;

  const handleButtonClick = () => {
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    const success = await submitUserFeedback(description);
    if (success) {
      setDescription("");
      setIsDialogOpen(false);
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setDescription("");
  };

  return (
    <>
      <div className="group" data-bug-report="true">
        <button
          onClick={() => void handleButtonClick()}
          className="flex items-center gap-2 h-10 rounded-full px-3
            bg-yellow-100 hover:bg-yellow-200 text-yellow-800
            dark:bg-yellow-950/80 dark:hover:bg-yellow-900 dark:text-yellow-200
            border border-yellow-300 dark:border-yellow-800/50
            shadow-md
            transition-all duration-300 ease-in-out
            w-10 group-hover:w-auto overflow-hidden"
          aria-label={t("common:bugReport.buttonLabel")}
        >
          <MessageCircleMore className="w-4 h-4 shrink-0" />
          <span className="whitespace-nowrap text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 pr-1">
            {t("common:userFeedback.buttonLabel")}
          </span>
        </button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md" data-bug-report="true">
          <DialogHeader>
            <DialogTitle>{t("common:userFeedback.dialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("common:userFeedback.dialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder={t("common:userFeedback.placeholder")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSubmit();
                }
              }}
              className="min-h-[120px]"
              maxLength={MAX_CHARS}
              autoFocus
            />
            <p className={`text-xs text-right ${description.length >= MAX_CHARS ? "text-destructive" : "text-muted-foreground"}`}>
              {MAX_CHARS - description.length} / {MAX_CHARS}
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={handleClose}>
              {t("common:buttons.cancel")}
            </Button>
            <Button
              onClick={() => void handleSubmit()}
              disabled={isSubmitting || !description.trim()}
            >
              {isSubmitting ? (
                <>
                  <Spinner className="size-4" />
                  {t("common:buttons.loading")}
                </>
              ) : (
                t("common:userFeedback.submitButton")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
